import { useEffect, useRef } from "react";
import { appMetadataPairshare as pairShareMetadata } from "src/pods/pairshare";
import DefaultPairShareResult from "src/pods/pairshare/components/sender/PairShareResult";
import { Question } from "src/pods/pairshare/types";
import { displayOpen, displayClose } from "src/pods/realtime";
import DefaultAppModal from "src/shared/components/AppModal";
import logEvent from "src/utils/logEvent";
import styles from "./DiscussContainer.module.css";

type DiscussContainerProps = {
  onBack: VoidFunction;
  displayOpen: typeof displayOpen;
  displayClose: typeof displayClose;
  question: Question;
  /**
   * DI for storybook stories — production callers leave undefined. Stubs
   * skip the pairshare runtime dependencies; tests just exercise this
   * container's surface. `logEvent` doesn't need DI — the toolkit Storybook
   * decorator already wires `setLogEventFunction(() => {})` globally.
   */
  di?: {
    AppModal?: React.ComponentType<React.ComponentProps<typeof DefaultAppModal>>;
    PairShareResult?: React.ComponentType<React.ComponentProps<typeof DefaultPairShareResult>>;
  };
};

export default function DiscussContainer({ onBack, displayOpen, displayClose, question, di }: DiscussContainerProps) {
  const AppModal = di?.AppModal ?? DefaultAppModal;
  const PairShareResult = di?.PairShareResult ?? DefaultPairShareResult;

  const openOverlayWithQuestion = (q: Question) => {
    displayOpen("/apps/display/ideas", {
      type: "SEND_IDEAS",
      payload: { watch: null, question: q },
    });
  };

  // Capture the props the mount/unmount effect cares about so the effect can
  // declare an empty dep array honestly (faithful to the pre-migration
  // componentDidMount/componentWillUnmount semantics) without tripping
  // `react-hooks/exhaustive-deps`. The refs always point to the latest values,
  // but the effect only ever reads them once (on mount, and again at unmount
  // for `displayClose`).
  const questionRef = useRef(question);
  questionRef.current = question;
  const displayOpenRef = useRef(displayOpen);
  displayOpenRef.current = displayOpen;
  const displayCloseRef = useRef(displayClose);
  displayCloseRef.current = displayClose;

  useEffect(() => {
    const q = questionRef.current;
    logEvent({ eventName: "ideas.pairshare.setup.open", metadata: { question: q } });
    displayOpenRef.current("/apps/display/ideas", {
      type: "SEND_IDEAS",
      payload: { watch: null, question: q },
    });

    return () => {
      logEvent({ eventName: "ideas.pairshare.setup.close" });
      displayCloseRef.current();
    };
  }, []);

  return (
    <article className={styles.app}>
      <AppModal
        {...pairShareMetadata}
        onRequestHide={onBack}
        onRequestStart={() => openOverlayWithQuestion(question)}
        onRequestStop={onBack}
      >
        <PairShareResult question={question} />
      </AppModal>
    </article>
  );
}
